/**
 * Header Component
 * App header with title, theme toggle, user info, and add link button
 */

import { useState } from "react";
import { Link2, Moon, Sun, Plus, LogOut, User, Copy, Check } from "lucide-react";
import { useTheme } from "next-themes";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import { getSessionToken } from "../services/authService";

interface HeaderProps {
  onAddClick: () => void;
}

export function Header({ onAddClick }: HeaderProps) {
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const [copied, setCopied] = useState(false);

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  const handleCopyToken = async () => {
    const token = getSessionToken();
    if (token) {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-16 items-center justify-between px-4 md:px-6">
        <div className="flex items-center gap-2">
          <Link2 className="h-6 w-6" />
          <div>
            <h1 className="text-xl font-bold">Link Storage</h1>
            <p className="hidden text-sm text-muted-foreground md:block">
              나만의 링크를 저장하고 관리하세요
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* User info */}
          {user && (
            <div className="hidden md:flex items-center gap-2 mr-2">
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name || user.email}
                  className="h-8 w-8 rounded-full"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                  <User className="h-4 w-4" />
                </div>
              )}
              <span className="text-sm text-muted-foreground">
                {user.name || user.email}
              </span>
            </div>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            aria-label="테마 변경"
            className="cursor-pointer"
          >
            <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          </Button>

          <Button onClick={onAddClick} className="cursor-pointer gap-2">
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">새 링크</span>
          </Button>

          {/* Copy token for extension */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCopyToken}
            aria-label="토큰 복사"
            className="cursor-pointer"
            title="익스텐션용 토큰 복사"
          >
            {copied ? (
              <Check className="h-5 w-5 text-green-500" />
            ) : (
              <Copy className="h-5 w-5" />
            )}
          </Button>

          {/* Logout button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={logout}
            aria-label="로그아웃"
            className="cursor-pointer"
            title="로그아웃"
          >
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </header>
  );
}
